import type { CellarSummary, TransferOffer } from "@corklog/shared";
import { useCallback, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { acceptTransfer, declineTransfer, fetchCellars } from "../api.js";
import { rememberCellars } from "../cellar.js";
import { actionButton } from "../components/controls.js";
import { useReload } from "../offline.js";
import { useSignedInUser } from "../session.js";

type State =
  | { status: "loading" }
  | { status: "ready"; cellars: CellarSummary[]; offers: TransferOffer[] }
  | { status: "failed" };

/**
 * Rows separated by hairlines, not cards. The visual model is a shelf runner:
 * a bottle lies on a shelf, a card floats. Plain state rather than TanStack
 * Query — one request, no invalidation, nothing to cache yet; the library
 * earns its place when the inventory arrives in phase 3.
 *
 * **One cellar is not a choice, so it is not offered as one.** Somebody with a
 * single cellar meets this screen twice on the way to every visit — once after
 * signing in, once on every press of the mark in the corner — and both times
 * the only thing it asks is whether they meant the cellar they have. They are
 * sent straight through instead.
 *
 * `replace` rather than a push, or the mark in the corner would become a trap:
 * going back from the cellar would land on the screen that immediately sends
 * you forward again. The list comes back on its own the day a second cellar
 * exists, which is the only day it has anything to say.
 *
 * **Being handed a cellar is a day it has something to say**, from `9.8`, and
 * the shortcut is suspended while an offer is waiting. Nothing is mailed, so
 * this is the only screen an offer can be sure of meeting — and a person with
 * one cellar of their own is exactly the person the shortcut would carry past
 * it, every time, forever.
 */
export function Cellars() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSignedInUser();
  const [state, setState] = useState<State>({ status: "loading" });
  /** Which offer is being answered, so neither button can be pressed twice. */
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    void fetchCellars()
      .then((response) => {
        // This list already knows the role in every cellar on it, so the page
        // opened from it does not have to ask again before it can decide what
        // to draw. Remembered whether or not this component is still mounted:
        // the answer arrived, and it is about the cellars and not about this
        // screen's lifetime.
        rememberCellars(response.cellars);
        setState({
          status: "ready",
          cellars: response.cellars,
          offers: response.offers,
        });
      })
      .catch(() => setState({ status: "failed" }));
  }, []);

  useReload(load);

  /**
   * Saying yes, and then standing in the cellar.
   *
   * Straight into it rather than back to a list that would now redirect there
   * anyway: what somebody has just accepted is a cellar, and the thing to do
   * with a cellar is open it. The role comes back with the answer, so the page
   * behind this knows who is reading before it draws.
   */
  const accept = (offer: TransferOffer) => {
    setBusy(offer.id);
    setFailed(false);
    void acceptTransfer(offer.id)
      .then((cellar) => {
        rememberCellars([cellar]);
        void navigate(`/kallare/${cellar.id}`, { replace: true });
      })
      .catch(() => {
        setFailed(true);
        setBusy(null);
        load();
      });
  };

  const decline = (offer: TransferOffer) => {
    setBusy(offer.id);
    setFailed(false);
    void declineTransfer(offer.id)
      .then(() => {
        setBusy(null);
        load();
      })
      .catch(() => {
        setFailed(true);
        setBusy(null);
      });
  };

  if (
    state.status === "ready" &&
    state.cellars.length === 1 &&
    state.offers.length === 0
  ) {
    return <Navigate to={`/kallare/${state.cellars[0]?.id ?? ""}`} replace />;
  }

  return (
    <section>
      <h1 className="font-display text-3xl">{t("cellars.title")}</h1>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}

      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("cellars.loadFailed")}
        </p>
      )}

      {/* The sentence about having no cellars is withheld when one is being
          offered, which is the one case where it argues with the screen it is
          on: *inga källare än, en källare skapas när kontot läggs upp* says
          there is nothing to be done here, directly above somebody handing
          this account a cellar. The offer is the answer to what is here. */}
      {state.status === "ready" &&
        (state.cellars.length === 0 ? (
          state.offers.length === 0 && (
            <p className="text-muted mt-6 text-sm">{t("cellars.empty")}</p>
          )
        ) : (
          <ul className="border-line mt-6 border-t">
            {state.cellars.map((cellar) => (
              <li
                key={cellar.id}
                className="border-line hover:bg-raised flex items-baseline justify-between border-b py-4"
              >
                <Link
                  to={`/kallare/${cellar.id}`}
                  className="font-display hover:text-accent text-lg underline-offset-4 hover:underline"
                >
                  {cellar.name}
                </Link>
                <span className="text-muted text-sm">{t(`role.${cellar.role}`)}</span>
              </li>
            ))}
          </ul>
        ))}

      {/* A cellar being handed over, below the ones already held rather than
          above them: what somebody came here to do is open their own cellar,
          and an offer is a rare visitor that should not push that down the
          screen. It is not in the list itself because it is not in the list —
          nothing here can be read until it is accepted.

          Both answers stand side by side and neither asks twice. Accepting is
          the deliberate act it looks like, on a paragraph that says what it
          costs the person offering; declining changes nothing, and the way
          back is them offering again. */}
      {state.status === "ready" &&
        state.offers.map((offer) => (
          <div key={offer.id} className="border-line mt-8 border-t pt-6">
            <h2 className="font-display text-xl">
              {t("transfer.offered", { cellar: offer.cellarName })}
            </h2>
            <p className="text-muted mt-2 max-w-prose text-sm">
              {t("transfer.offeredBy", {
                name: offer.fromName,
                email: offer.fromEmail,
              })}
            </p>
            <p className="text-muted mt-2 max-w-prose text-sm">
              {t("transfer.offerMeans")}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => accept(offer)}
                className={actionButton}
              >
                {t("transfer.accept")}
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => decline(offer)}
                className="text-muted hover:text-past text-sm underline-offset-4 hover:underline"
              >
                {t("transfer.decline")}
              </button>
            </div>
          </div>
        ))}

      {failed && (
        <p role="alert" className="text-past mt-4 text-sm">
          {t("transfer.answerFailed")}
        </p>
      )}

      {/* *Upptäck* belongs to no cellar. Since 2026-10-07 it is reached from a
          cellar's *Mer* rather than from the header, so the reader who has not
          opened a cellar yet — the only one who sees this page — is offered it
          here, under the same switch. */}
      {user?.mayDiscover === true && (
        <p className="mt-8 text-sm">
          <Link to="/upptack" className="text-accent underline-offset-4 hover:underline">
            {t("discover.title")}
          </Link>
        </p>
      )}
    </section>
  );
}
